import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { Download as DownloadIcon } from '@mui/icons-material';
import useExpenseLookups from '../../components/expense/useExpenseLookups';
import { formatDate, STATUS_META } from '../../services/expenseVouchersApi';
import { registerApi, reportsApi } from '../../services/expenseAccountsApi';
import { VoucherStatusChip } from '../../components/expense/voucherShared';
import { DateRangeFilter, EmployeeFilter } from '../../components/expense/AccountsFilters';
import { FilterBar, ReportsHeader, ReportTable, useReport } from '../../components/expense/ReportShared';

const EMPTY_FILTERS = { vendor: '', employeeId: '', status: '', currency: '', approvedFrom: '', approvedTo: '', submittedFrom: '', submittedTo: '' };
// Drafts were never submitted: only their owner may see their bills
const STATUS_OPTIONS = Object.entries(STATUS_META).filter(([value]) => value !== 'DRAFT');

const mb = (bytes) => `${(bytes / 1048576).toFixed(bytes < 1048576 ? 2 : 1)} MB`;
// Register filters only (no paging / sorting), empty values dropped
const filterBody = (params) => Object.fromEntries(Object.entries(params)
  .filter(([k, v]) => v !== '' && v !== undefined && !['sort', 'order', 'page', 'limit'].includes(k)));

// Bulk Bill Download: select vouchers (or use the filters) and download their bills as one
// ZIP. The server only includes bills it holds on submitted vouchers and reports every bill
// it could not include.
const AccountsBulkBills = () => {
  const { currencies, users } = useExpenseLookups(['currencies', 'users']);
  const report = useReport(registerApi.list, { emptyFilters: EMPTY_FILTERS, defaultSort: { sort: 'voucherDate', order: 'desc' } });
  const { filters, setFilter, setFilterValue, rows } = report;
  const [selected, setSelected] = useState(() => new Map()); // id -> voucher number
  const [pending, setPending] = useState(null); // { body, summary, label }
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const selectable = rows.filter((r) => r.status !== 'DRAFT' && r.billCount > 0);
  const allOnPage = selectable.length > 0 && selectable.every((r) => selected.has(r._id));
  const toggle = (r) => setSelected((s) => {
    const next = new Map(s);
    if (next.has(r._id)) next.delete(r._id); else next.set(r._id, r.voucherNumber);
    return next;
  });
  const togglePage = () => setSelected((s) => {
    const next = new Map(s);
    selectable.forEach((r) => (allOnPage ? next.delete(r._id) : next.set(r._id, r.voucherNumber)));
    return next;
  });

  const prepare = async (body, label) => {
    setBusy(true);
    setResult(null);
    try {
      const res = await reportsApi.billSummary(body);
      setPending({ body, label, summary: res.data });
    } catch (e) {
      setResult({ severity: 'error', message: e.message });
    } finally {
      setBusy(false);
    }
  };

  const download = async () => {
    setBusy(true);
    try {
      const r = await reportsApi.downloadBills(pending.body);
      const problems = r.missing + r.failed;
      setResult({
        severity: problems ? 'warning' : 'success',
        message: `Downloaded ${r.downloaded} bill${r.downloaded === 1 ? '' : 's'} (${r.filename}). Missing: ${r.missing}. Failed: ${r.failed}.${problems ? ' See _Download_Summary.csv in the ZIP for each bill.' : ''}`,
      });
      setPending(null);
    } catch (e) {
      setResult({ severity: 'error', message: e.message });
      setPending(null);
    } finally {
      setBusy(false);
    }
  };

  const columns = [
    { label: 'Voucher No.', sortKey: 'voucherNumber', nowrap: true, render: (r) => <Typography variant="body2" sx={{ fontWeight: 600 }}>{r.voucherNumber}</Typography> },
    { label: 'Voucher Date', sortKey: 'voucherDate', nowrap: true, render: (r) => formatDate(r.voucherDate) },
    { label: 'Employee', sortKey: 'employee', render: (r) => r.employeeName },
    { label: 'Vendor', render: (r) => r.vendors.join(', ') || '-' },
    { label: 'Currency', render: (r) => r.currency || '-' },
    { label: 'Status', sortKey: 'status', render: (r) => <VoucherStatusChip status={r.status} /> },
    { label: 'Bills', align: 'right', render: (r) => r.billCount },
  ];

  return (
    <Box sx={{ bgcolor: 'background.default', p: 3 }}>
      <ReportsHeader
        title="Bill Download"
        subtitle="Download the bills of selected vouchers, or of every voucher matching the filters, as one ZIP."
        actions={(
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button variant="contained" startIcon={<DownloadIcon />} disabled={busy || selected.size === 0} sx={{ textTransform: 'none' }}
              onClick={() => prepare({ voucherIds: [...selected.keys()] }, `${selected.size} selected voucher${selected.size === 1 ? '' : 's'}`)}>
              Download selected ({selected.size})
            </Button>
            <Button variant="outlined" startIcon={<DownloadIcon />} disabled={busy || report.total === 0} sx={{ textTransform: 'none' }}
              onClick={() => prepare(filterBody(report.params), 'all vouchers matching the filters')}>
              Download all matching
            </Button>
          </Box>
        )}
      />

      {result && <Alert severity={result.severity} sx={{ mb: 2 }} onClose={() => setResult(null)} data-testid="bill-result">{result.message}</Alert>}

      <FilterBar onClear={report.clear}>
        <TextField size="small" placeholder="Search voucher, invoice, employee, vendor..." value={report.search} onChange={(e) => report.setSearch(e.target.value)} sx={{ minWidth: 280 }} />
        <EmployeeFilter users={users} value={filters.employeeId} onChange={setFilterValue('employeeId')} />
        <TextField size="small" label="Vendor" placeholder="Vendor name" value={filters.vendor} onChange={setFilter('vendor')} sx={{ minWidth: 170 }} />
        <TextField select size="small" label="Status" value={filters.status} onChange={setFilter('status')} sx={{ minWidth: 170 }}>
          <MenuItem value="">All submitted</MenuItem>
          {STATUS_OPTIONS.map(([value, m]) => <MenuItem key={value} value={value}>{m.label}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Currency" value={filters.currency} onChange={setFilter('currency')} sx={{ minWidth: 130 }}>
          <MenuItem value="">All</MenuItem>
          {currencies.map((c) => <MenuItem key={c.value} value={c.value}>{c.value}</MenuItem>)}
        </TextField>
        <DateRangeFilter label="Submitted" from={filters.submittedFrom} to={filters.submittedTo}
          onFromChange={setFilterValue('submittedFrom')} onToChange={setFilterValue('submittedTo')} />
        <DateRangeFilter label="Approved" from={filters.approvedFrom} to={filters.approvedTo}
          onFromChange={setFilterValue('approvedFrom')} onToChange={setFilterValue('approvedTo')} />
        {selected.size > 0 && <Button onClick={() => setSelected(new Map())} sx={{ textTransform: 'none' }}>Clear selection</Button>}
      </FilterBar>

      <ReportTable
        report={report}
        columns={columns}
        emptyText="No vouchers match."
        before={{
          header: <Checkbox size="small" sx={{ color: 'white' }} checked={allOnPage} disabled={!selectable.length} onChange={togglePage} inputProps={{ 'aria-label': 'Select all on this page' }} />,
          cell: (r) => {
            const disabled = r.status === 'DRAFT' || !r.billCount;
            return <Checkbox size="small" checked={selected.has(r._id)} disabled={disabled} onChange={() => toggle(r)} inputProps={{ 'aria-label': `Select ${r.voucherNumber}` }} />;
          },
        }}
      />

      <Dialog open={Boolean(pending)} onClose={() => !busy && setPending(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Download bills</DialogTitle>
        {pending && (
          <DialogContent>
            <Typography variant="body2" sx={{ mb: 1 }}>For {pending.label}:</Typography>
            <Typography variant="body2" data-testid="bill-summary">
              {pending.summary.bills} bill{pending.summary.bills === 1 ? '' : 's'} from {pending.summary.vouchers} voucher{pending.summary.vouchers === 1 ? '' : 's'} ({mb(pending.summary.bytes)})
            </Typography>
            {pending.summary.unavailableVouchers > 0 && (
              <Alert severity="info" sx={{ mt: 1 }}>{pending.summary.unavailableVouchers} selected voucher(s) have no downloadable bills (draft, deleted or without bills).</Alert>
            )}
            {pending.summary.tooLarge && <Alert severity="error" sx={{ mt: 1 }}>{pending.summary.tooLarge}</Alert>}
            {!pending.summary.bills && <Alert severity="warning" sx={{ mt: 1 }}>There are no bills to download.</Alert>}
          </DialogContent>
        )}
        <DialogActions>
          <Button onClick={() => setPending(null)} disabled={busy}>Cancel</Button>
          <Button variant="contained" onClick={download} disabled={busy || !pending?.summary.bills || Boolean(pending?.summary.tooLarge)}
            startIcon={busy ? <CircularProgress size={16} /> : <DownloadIcon />}>
            Download ZIP
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AccountsBulkBills;
